// covered-by: qa/rickrubin/tests/api/ask-puntt-share.spec.ts
import {
  type AskPunttChatScope,
  type ListAskPunttChatsResponse,
  type GetAskPunttChatHistoryResponse,
  type CreateAskPunttChatResponse,
  type SendAskPunttMessageResponse,
  type DeleteAskPunttChatResponse,
  type AbortAskPunttMessageResponse,
  type AskPunttProjectData,
  type SearchEnterpriseUsersResponse,
  type ShareAskPunttChatResponse,
  type UnshareAskPunttChatResponse,
  type UpdateAskPunttChatResponse,
} from "@mg/schemas/src/christo/askPuntt";

import { getUsers } from "./users";

import { API_URL } from "../config/env";
import { smartFetch } from "../utils/http";

/**
 * Ask Puntt Service - API client for the Ask Puntt chat feature
 */

export async function listChats(params?: {
  limit?: number;
  offset?: number;
  scope?: AskPunttChatScope;
}): Promise<ListAskPunttChatsResponse> {
  const queryParams = new URLSearchParams();
  if (params?.limit != null) queryParams.set("limit", String(params.limit));
  if (params?.offset != null) queryParams.set("offset", String(params.offset));
  if (params?.scope) queryParams.set("scope", params.scope);

  const queryString = queryParams.toString();
  const url = `${API_URL}/private/catalyst/ask-puntt/chats${queryString ? `?${queryString}` : ""}`;

  const json = await smartFetch(url, { method: "GET" });
  return json as ListAskPunttChatsResponse;
}

export async function getChatHistory(
  chatId: string,
): Promise<GetAskPunttChatHistoryResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}`,
    { method: "GET" },
  );
  return json as GetAskPunttChatHistoryResponse;
}

export async function getChatEnterpriseId(
  chatId: string,
): Promise<{ enterpriseId: string }> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/enterprise`,
    { method: "GET" },
  );
  return json as { enterpriseId: string };
}

export async function createChat(payload: {
  message: string;
  workflowType?: string;
  attachments?: { key: string; source: string; type: string; name?: string }[];
  enabledConnectors?: string[];
  // Set when a workflow card is opened from the home page (no attachments yet):
  // the backend replies with the skill's canned greeting and skips the LLM.
  workflowGreeting?: boolean;
  // Project linkage (Ask Puntt Projects). When set, the new chat is created
  // inside a project. `projectChatKind` distinguishes the team-visible shared
  // chat from a member's private personal chat. Omitted → standalone chat
  // (legacy behavior, unchanged).
  projectId?: string;
  projectChatKind?: "shared" | "personal";
}): Promise<CreateAskPunttChatResponse> {
  const json = await smartFetch(`${API_URL}/private/catalyst/ask-puntt/chats`, {
    method: "POST",
    body: JSON.stringify(payload),
  });
  return json as CreateAskPunttChatResponse;
}

export async function getMessageStatus(
  chatId: string,
  messageId: string,
): Promise<import("@mg/schemas/src/christo/askPuntt").AskPunttMessage> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/messages/${messageId}/status`,
    { method: "GET" },
  );
  return json as import("@mg/schemas/src/christo/askPuntt").AskPunttMessage;
}

export async function submitFeedback(
  chatId: string,
  messageId: string,
  feedback: "thumbs_up" | "thumbs_down",
): Promise<{ success: boolean }> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/messages/${messageId}/feedback`,
    {
      method: "PATCH",
      body: JSON.stringify({ feedback }),
    },
  );
  return json as { success: boolean };
}

export async function deleteChat(
  chatId: string,
): Promise<DeleteAskPunttChatResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}`,
    { method: "DELETE" },
  );
  return json as DeleteAskPunttChatResponse;
}

export async function renameChat(
  chatId: string,
  title: string,
): Promise<UpdateAskPunttChatResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}`,
    {
      method: "PATCH",
      body: JSON.stringify({ title }),
    },
  );
  return json as UpdateAskPunttChatResponse;
}

export async function sendMessage(
  chatId: string,
  payload: {
    message: string;
    attachments?: {
      key: string;
      source: string;
      type: string;
      name?: string;
    }[];
    sectionContext?: { id: string; title: string };
    mentions?: string[];
    mentionEmails?: string[];
  },
): Promise<SendAskPunttMessageResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/messages`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
  return json as SendAskPunttMessageResponse;
}

export async function updateProjectAction(
  chatId: string,
  action: "override_stage" | "complete_project",
  stageName?: string,
): Promise<{ success: boolean; projectData: AskPunttProjectData }> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/project/action`,
    {
      method: "PATCH",
      body: JSON.stringify({ action, stageName }),
    },
  );
  return json as { success: boolean; projectData: AskPunttProjectData };
}

/**
 * Phase 5: Lightweight streaming state polling.
 * Returns only the fields needed during streaming instead of the full chat document.
 */
export async function getMessageStreamingState(
  chatId: string,
  messageId: string,
): Promise<{
  status: string;
  content: string;
  streamingContent: string | null;
  agentSteps: unknown[];
  searchProgress: unknown[];
  canvasContent: unknown | null;
  canvasLoading: boolean;
  canvasChangedSectionIds: string[];
  viewTicketId: string | null;
  suggestedResponses: string[];
  projectData: unknown | null;
  projectLoading: boolean;
  contentBlocks: unknown[] | null;
}> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/messages/${messageId}/stream`,
    { method: "GET" },
  );
  return json as Awaited<ReturnType<typeof getMessageStreamingState>>;
}

export async function abortMessage(
  chatId: string,
  messageId: string,
): Promise<AbortAskPunttMessageResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/messages/${messageId}/abort`,
    { method: "POST" },
  );
  return json as AbortAskPunttMessageResponse;
}

export async function shareChat(
  chatId: string,
  payload: { userIds?: string[]; emails?: string[] },
): Promise<ShareAskPunttChatResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/share`,
    {
      method: "POST",
      body: JSON.stringify(payload),
    },
  );
  return json as ShareAskPunttChatResponse;
}

export async function unshareChat(
  chatId: string,
  userId: string,
): Promise<UnshareAskPunttChatResponse> {
  const json = await smartFetch(
    `${API_URL}/private/catalyst/ask-puntt/chats/${chatId}/share/${userId}`,
    { method: "DELETE" },
  );
  return json as UnshareAskPunttChatResponse;
}

function rosterFromGetUsers(
  limit: number,
): Promise<SearchEnterpriseUsersResponse> {
  return getUsers().then((users) => ({
    users: users
      .slice()
      .sort((a, b) =>
        (a.name ?? a.email ?? "").localeCompare(b.name ?? b.email ?? ""),
      )
      .slice(0, limit)
      .map((u) => ({
        _id: u._id,
        ...(u.name != null ? { name: u.name } : {}),
        ...(u.email != null ? { email: u.email } : {}),
        ...(u.avatar !== undefined ? { avatar: u.avatar ?? null } : {}),
      })),
  }));
}

/**
 * Server-side enterprise user search by name/email substring. Used by the
 * Share dialog, @mention composer, and other "pick a user" surfaces.
 * Bare `@` (empty `q`) returns the first page of the enterprise roster.
 */
export async function searchEnterpriseUsers(
  q: string,
  limit?: number,
): Promise<SearchEnterpriseUsersResponse> {
  const trimmed = (q ?? "").trim();
  const cap = limit ?? 25;
  const queryParams = new URLSearchParams();
  queryParams.set("q", trimmed);
  queryParams.set("limit", String(cap));
  try {
    const json = await smartFetch(
      `${API_URL}/private/catalyst/users/search?${queryParams.toString()}`,
      { method: "GET" },
    );
    return json as SearchEnterpriseUsersResponse;
  } catch (err) {
    // Older christo builds rejected empty `q` with 400 before the mention
    // composer shipped. Fall back to the roster GET so bare `@` still works.
    if (trimmed.length === 0 && err instanceof Response && err.status === 400) {
      return rosterFromGetUsers(cap);
    }
    throw err;
  }
}
